import { find, get } from "lodash";
import { matchPath } from "react-router";

import { DomainNames } from "services/app/types";

import * as routes from "./index";
import { RouteConfiguration, RouteInfo } from "./types";

const kywBackground = "rgb(247, 247, 247)";

const defaultConfig: RouteConfiguration = {
  bodyClass: "react-container",
  buildTitle: () => "Know Your Worth",
  showNav: false,
  showFooter: true,
  scrollToTop: true,
  background: kywBackground,
};

const orderBookConfig: RouteConfiguration = {
  ...defaultConfig,
  bodyClass: "overview",
};

const sellerConfig: RouteConfiguration = {
  ...orderBookConfig,
  bodyClass: null,
};

const orderBookOverviewConfig: RouteConfiguration = {
  ...orderBookConfig,
  showNav: true,
  background: "transparent",
};

const dealConfig: RouteConfiguration = {
  ...orderBookConfig,
  bodyClass: "overview auction-category orderbook",
  buildTitle: () => "Deal Hub",
};

const auctionHouseConfig: RouteConfiguration = {
  ...defaultConfig,
  bodyClass: "overview -sidebar auction-category",
};

const dashboardConfig: RouteConfiguration = {
  ...defaultConfig,
  showNav: true,
  bodyClass: "dashboard",
  background: "transparent",
};

const routesConfig: RouteInfo[] = [
  // Overview
  {
    domain: "auctions",
    routeName: routes.overview(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      background: kywBackground,
      buildTitle: () => "Overview",
    },
  },

  // Directory
  {
    domain: "auctions",
    routeName: routes.directoryLogin(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directoryLogout(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directorySignUp(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directoryFinalizeSetup(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directorySignInOrRegister(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directoryEmailVerificationSent(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directoryProfile(),
    config: {
      ...defaultConfig,
      bodyClass: "full-width-orderbook",
      buildTitle: () => "Profile",
      background: kywBackground,
      showFooter: false,
      showNav: true,
    },
  },

  {
    domain: "auctions",
    routeName: routes.directoryChangePassword(),
    config: defaultConfig,
  },

  {
    domain: "auctions",
    routeName: routes.directoryBuyerDashboard(),
    config: {
      ...dashboardConfig,
    },
  },

  {
    domain: "auctions",
    routeName: routes.smsNotificationsOptIn(),
    config: {
      ...orderBookConfig,
      showFooter: false,
      background: "white",
      buildTitle: () => "Opt-in to offer notifications by SMS",
    },
  },
  // Order Book
  // Exchange
  {
    domain: "auctions",
    routeName: routes.orderBookMyListingsNoParams(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      background: kywBackground,
      buildTitle: () => "My Listings | The Exchange",
      scrollToTop: false,
    },
  },
  {
    domain: "auctions",
    routeName: routes.orderBookOpenListingsNoParams(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      background: kywBackground,
      buildTitle: () => "Open Listings | The Exchange",
      scrollToTop: false,
    },
  },
  {
    domain: "auctions",
    routeName: routes.orderBookPastDealsNoParams(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      background: kywBackground,
      buildTitle: () => "Past Deals | The Exchange",
      scrollToTop: false,
    },
  },
  {
    domain: "auctions",
    routeName: routes.orderBookAssetDetail(),
    config: {
      ...orderBookOverviewConfig,
      bodyClass: "full-width-orderbook",
      buildTitle: path => `Listing ID (${path.split("/")[3]})`,
      scrollToTop: false,
    },
  },
  {
    domain: "auctions",
    routeName: routes.orderBookDealPage(),
    config: {
      ...dealConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
    },
  },
  // Buyer
  {
    domain: "auctions",
    routeName: routes.orderBookBuyerDashboard(),
    config: {
      ...orderBookOverviewConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      background: kywBackground,
      buildTitle: () => "Buyer Dashboard",
      scrollToTop: false,
    },
  },
  {
    domain: "auctions",
    routeName: routes.orderBookOrderFill(),
    config: {
      ...orderBookOverviewConfig,
      buildTitle: () => "Order Fill",
    },
  },
  // Seller
  // Utilities
  {
    domain: "worth",
    routeName: routes.sellerNotFound(),
    config: {
      ...sellerConfig,
      background: "transparent",
      showNav: true,
    },
  },
  // Create account/login
  {
    domain: "worth",
    routeName: routes.sellerCreateAccount(),
    config: {
      ...sellerConfig,
    },
  },
  {
    domain: "worth",
    routeName: routes.sellerCreateAccountReferral(),
    config: {
      ...sellerConfig,
    },
  },
  {
    domain: "worth",
    routeName: routes.sellerCompleteAccount(),
    config: {
      ...sellerConfig,
    },
  },
  {
    domain: "worth",
    routeName: routes.sellerLogin(),
    config: {
      ...sellerConfig,
    },
  },
  // Dashboard
  {
    domain: "worth",
    routeName: routes.sellerDashboard(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      buildTitle: () => "My Royalties",
      background: kywBackground,
    },
  },
  // Connect routes
  {
    domain: "worth",
    routeName: routes.sellerAboutDealOptions(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      bodyClass: "full-width-orderbook",
      buildTitle: () => "Deal Options",
      background: kywBackground,
    },
  },
  {
    domain: "worth",
    routeName: routes.sellerAddRoyaltyAccount(),
    config: {
      ...auctionHouseConfig,
      bodyClass: "full-width-orderbook",
      buildTitle: () => "Add Royalty Accounts",
      background: kywBackground,
      showFooter: false,
    },
  },
  {
    domain: "worth",
    routeName: routes.sellerValuationLoading(),
    config: {
      ...orderBookConfig,
      scrollToTop: false,
    },
  },
  {
    domain: "worth",
    routeName: routes.sellerValuationResults(),
    config: {
      ...orderBookConfig,
      showFooter: false,
      bodyClass: "overview valuation-results-body",
    },
  },
  // Document Upload
  {
    domain: "worth",
    routeName: routes.sellerDocumentUpload(),
    config: {
      ...auctionHouseConfig,
      background: "transparent",
      buildTitle: () => "Royalty Exchange Valuation Portal",
      bodyClass: `${auctionHouseConfig.bodyClass} full-width-orderbook`,
    },
  },
  // Preview
  {
    domain: "auctions",
    routeName: routes.previewValuation(),
    config: {
      ...orderBookConfig,
      showFooter: false,
      bodyClass: "overview valuation-results-body",
    },
  },
  {
    domain: "auctions",
    routeName: routes.previewValuationLoading(),
    config: {
      ...orderBookConfig,
    },
  },
  {
    domain: "auctions",
    routeName: routes.previewOrderFill(),
    config: {
      ...orderBookOverviewConfig,
      buildTitle: () => "Order Fill",
    },
  },
  // Admin
  {
    domain: "auctions",
    routeName: routes.adminLogin(),
    config: defaultConfig,
  },
  // Plaid Verification
  {
    domain: "auctions", // or "worth"
    routeName: routes.orderBookVerify(),
    config: {
      ...auctionHouseConfig,
      background: "transparent",
      buildTitle: () => "Royalty Exchange Investor Verification",
      bodyClass: `${auctionHouseConfig.bodyClass} full-width-orderbook`,
    },
  },
  // Stripe/Plaid Payments
  {
    domain: "auctions",
    routeName: routes.orderBookPay(),
    config: {
      ...auctionHouseConfig,
      showNav: true,
      buildTitle: () => "Royalty Exchange Payment Portal",
      bodyClass: `${auctionHouseConfig.bodyClass} full-width-orderbook`,
    },
  },
];

const defaultRouteInfo: RouteInfo = {
  domain: "auctions",
  routeName: "",
  config: defaultConfig,
};

export const routeEquals = (route1: string, route2: string) => {
  return (
    matchPath(route1, {
      path: route2,
    }) != null
  );
};

const matchRoute = (route: string) => (routeInfo: RouteInfo) => {
  return routeEquals(route, routeInfo.routeName);
};

export const getRouteInfoFromRouteName = (routeName: string) => {
  return find(routesConfig, matchRoute(routeName)) || defaultRouteInfo;
};

export const getPageURL = (routeName: string, domainNames?: DomainNames) => {
  const routeInfo = getRouteInfoFromRouteName(routeName);
  const domainName = get(domainNames, routeInfo.domain, "");
  return domainName + routeName;
};
